import React, { useCallback } from 'react';
import { useCategoriesQuery, CategoriesQuery } from 'graphql/types';
import CategoryCollapseTree from '../categoryCollapseTree';
import { CategoryNameLabel } from '../categories/categoryNameLabel';
import { CategoryOption, CategoryOptionLabel, CategoryCheckbox } from './index.styled';

interface CheckboxOption {
  label: string;
  description?: string;
  value: string;
}

interface DetailedCheckboxProps {
  onChange?: (ids: string[]) => void;
  options: CheckboxOption[];
  defaultValue?: string[];
  disabled?: boolean;
  value?: string[];
}

type Category = CategoriesQuery['categories']['data'][0];

function DetailedCheckbox({ onChange = () => { }, value = [] }: DetailedCheckboxProps) {
  const { data } = useCategoriesQuery();

  const onValueChange = useCallback(
    (id: string) => {
      if (value.includes(id)) {
        onChange(value.filter((item) => item !== id));
      } else {
        onChange([...value, id]);
      }
    },
    [onChange, value],
  );

  const renderCategoryOption = (category: Category) => {
    const { title, translatedName } = category.attributes ?? {};
    const categoryId = String(category.id);
    const checked = value.includes(categoryId);

    return (
      <CategoryOption
        key={categoryId}
        $checked={checked}
        onClick={() => onValueChange(categoryId)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onValueChange(categoryId);
          }
        }}
      >
        <CategoryCheckbox
          checked={checked}
          onClick={(e) => e.stopPropagation()}
          onChange={() => onValueChange(categoryId)}
        />
        <CategoryOptionLabel>
          <CategoryNameLabel title={title} translatedName={translatedName} />
        </CategoryOptionLabel>
      </CategoryOption>
    );
  };

  return (
    <CategoryCollapseTree<Category>
      categories={data?.categories?.data ?? []}
      renderItem={renderCategoryOption}
    />
  );
}

export default DetailedCheckbox;
